import { fromTradingNetwork } from '@entities/network';
import { formatDisplayAmount, formatUnits } from '@shared/lib/format';
import { shortenAddress } from '@shared/lib/utils/address';
import type { TokenMetaLookup } from '@entities/token';
import type { LimitOrder } from './order.schema';

export interface OrderRow {
  id: string;
  type: string;
  pair: string;
  price: string;
  amount: string;
  progress: number;
  status: string;
}

/** Lookup is chain-aware: a Solana address must not be lowercased to match (base58 is case-sensitive). */
export function limitOrderToRow(o: LimitOrder, lookup: TokenMetaLookup): OrderRow {
  const chainId = fromTradingNetwork(o.network) ?? o.network;
  const from = lookup(chainId, o.fromToken);
  const to = lookup(chainId, o.toToken);
  const fromSym = from?.symbol ?? shortenAddress(o.fromToken);
  const toSym = to?.symbol ?? shortenAddress(o.toToken);

  const fromDecimals = from?.decimals ?? null;
  const toDecimals = to?.decimals ?? null;

  const fromAmount = formatDisplayAmount(o.fromAmount, fromDecimals);
  const amount = `${fromAmount.text}${fromAmount.isRaw ? ' (raw)' : ''} ${fromSym}`;

  let price = o.triggerPriceUsd ?? '—';
  // A price ratio needs both exponents; with either unknown it would be off by orders of magnitude.
  if (!o.triggerPriceUsd && fromDecimals !== null && toDecimals !== null) {
    const f = Number(formatUnits(o.fromAmount, fromDecimals));
    const t = Number(formatUnits(o.toAmount, toDecimals));
    price = f > 0 ? (t / f).toFixed(4) : '—';
  }

  return {
    id: o.id,
    type: o.orderType,
    pair: `${fromSym}/${toSym}`,
    price,
    amount,
    progress: o.status === 'filled' ? 100 : 0,
    status: o.status,
  };
}
